import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { toggleVacationMode as toggleVacationModeApi } from '../../util/api';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const TOGGLE_VACATION_MODE_REQUEST = 'app/ProfileSettingsPage/UPDATE_VACATION_MODE_REQUEST';
export const TOGGLE_VACATION_MODE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_VACATION_MODE_SUCCESS';
export const TOGGLE_VACATION_MODE_ERROR = 'app/ProfileSettingsPage/UPDATE_VACATION_MODE_ERROR';

export const TOGGLE_NOTIFICATIONS_REQUEST = 'app/ProfileSettingsPage/UPDATE_NOTIFICATIONS_REQUEST';
export const TOGGLE_NOTIFICATIONS_SUCCESS = 'app/ProfileSettingsPage/UPDATE_NOTIFICATIONS_SUCCESS';
export const TOGGLE_NOTIFICATIONS_ERROR = 'app/ProfileSettingsPage/UPDATE_NOTIFICATIONS_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  toggleVacationModeInProgress: false,
  toggleVacationModeError: null,
  toggleNotificationsInProgress: false,
  toggleNotificationsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case TOGGLE_VACATION_MODE_REQUEST:
      return {
        ...state,
        toggleVacationModeInProgress: true,
        toggleVacationModeError: null,
      };
    case TOGGLE_VACATION_MODE_SUCCESS:
      return {
        ...state,
        toggleVacationModeInProgress: false,
      };
    case TOGGLE_VACATION_MODE_ERROR:
      return {
        ...state,
        toggleVacationModeInProgress: false,
        toggleVacationModeError: payload,
      };
    case TOGGLE_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        toggleNotificationsInProgress: true,
        toggleNotificationsError: null,
      };
    case TOGGLE_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        toggleNotificationsInProgress: false,
      };
    case TOGGLE_NOTIFICATIONS_ERROR:
      return {
        ...state,
        toggleNotificationsInProgress: false,
        toggleNotificationsError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

const toggleVacationModeRequest = () => ({
  type: TOGGLE_VACATION_MODE_REQUEST,
});

const toggleVacationModeSuccess = () => ({
  type: TOGGLE_VACATION_MODE_SUCCESS,
});

const toggleVacationModeError = error => ({
  type: TOGGLE_VACATION_MODE_ERROR,
  payload: error,
  error: true,
});

const toggleNotificationsRequest = () => ({
  type: TOGGLE_NOTIFICATIONS_REQUEST,
});

const toggleNotificationsSuccess = () => ({
  type: TOGGLE_NOTIFICATIONS_SUCCESS,
});

const toggleNotificationsError = error => ({
  type: TOGGLE_NOTIFICATIONS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const { id } = actionPayload;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

export const toggleVacationMode = () => async dispatch => {
  dispatch(toggleVacationModeRequest());
  try {
    await toggleVacationModeApi();
    dispatch(fetchCurrentUser());
    dispatch(toggleVacationModeSuccess());
  } catch (e) {
    dispatch(toggleVacationModeError(storableError(e)));
  }
};

export const toggleNotifications = value => async (dispatch, getState, sdk) => {
  dispatch(toggleNotificationsRequest());
  try {
    const { currentUser } = getState().user;
    const { notifications } = currentUser?.attributes?.profile?.privateData || {};

    await sdk.currentUser.updateProfile({
      privateData: {
        notifications: {
          ...notifications,
          ...value,
        },
      },
    });
    dispatch(fetchCurrentUser());
    return dispatch(toggleNotificationsSuccess());
  } catch (e) {
    return dispatch(toggleNotificationsError(storableError(e)));
  }
};
